/**
 * Renders the browser stopped waiting for — the tab was closed, the wait ran out — are
 * still rendering on the server. Looking them up again finishes them in the gallery
 * instead of leaving them spinning forever.
 */
import { checkRender, type Transport } from './aiRender';
import type { GalleryItem } from './gallery';
import { t } from '../i18n/I18n';

/** After this long without an answer the render is written off. */
export const RESUME_GIVE_UP_MS = 30 * 60_000;
/** A shot whose upload never produced a server id is written off sooner. */
const UNSENT_GIVE_UP_MS = 5 * 60_000;

export interface ResumeDeps {
  transport: Transport;
  check?: typeof checkRender;
  now?: () => number;
}

/** One look at an unfinished render: the patch to apply, or null while it is still running. */
export async function resumeRender(item: GalleryItem, deps: ResumeDeps): Promise<Partial<GalleryItem> | null> {
  if (item.kind !== 'render' || item.status !== 'running') return null;
  const now = deps.now?.() ?? Date.now();
  const age = now - item.createdAt;
  if (!item.renderId) return age > UNSENT_GIVE_UP_MS ? { status: 'error', error: t('#RenderResumeNeverSent') } : null;
  try {
    const check = await (deps.check ?? checkRender)(item.renderId, deps.transport);
    if (check.state === 'done') {
      const { result } = check;
      return { status: 'done', imageUrl: result.imageUrl, thumbUrl: result.thumbUrl, initImageUrl: result.initImageUrl, width: result.width ?? item.width, height: result.height ?? item.height, error: undefined };
    }
    if (check.state === 'error') return { status: 'error', error: check.error };
  } catch {
    // No answer from the server is not a verdict on the render.
  }
  return age > RESUME_GIVE_UP_MS ? { status: 'error', error: t('#RenderResumeGaveUp') } : null;
}

/** Look up every unfinished render in the list and apply what changed; returns how many did. */
export async function resumeRunning(items: GalleryItem[], apply: (id: string, patch: Partial<GalleryItem>) => Promise<unknown>, deps: ResumeDeps): Promise<number> {
  let changed = 0;
  for (const item of items) {
    if (item.kind !== 'render' || item.status !== 'running') continue;
    const patch = await resumeRender(item, deps);
    if (patch) { await apply(item.id, patch); changed++; }
  }
  return changed;
}
